/**
 * Clerk identify bridge — call `identify()` with the Clerk user ID
 * whenever sign-in state changes, and `reset()` on sign-out.
 *
 * Drop `<ClerkIdentifyBridge />` anywhere inside your `<ClerkProvider>`
 * (or `<AuthProvider>` from `@engagexp/auth`, which wraps Clerk).
 *
 * Imports `@clerk/clerk-react` — only available via the
 * `@engagexp/analytics/clerk` subpath export, so apps that don't use
 * Clerk never pay the cost.
 *
 * LGPD: we only pass the stable Clerk user ID as distinct_id. We do NOT
 * forward email, name, or any other PII to PostHog — even though Clerk's
 * user object exposes them, they're filtered by the tracker's PII guard.
 *
 * @example
 * ```tsx
 * import { ClerkProvider } from "@clerk/clerk-react";
 * import { ClerkIdentifyBridge } from "@engagexp/analytics/clerk";
 *
 * <ClerkProvider publishableKey={...}>
 *   <AnalyticsProvider product="gamin_index" ...>
 *     <ClerkIdentifyBridge />
 *     <RouterProvider router={router} />
 *   </AnalyticsProvider>
 * </ClerkProvider>
 * ```
 */
import { useEffect } from "react";
import { useAuth } from "@clerk/clerk-react";
import { identify, reset } from "../tracker";

/**
 * Invisible component that wires Clerk auth state to PostHog identify/reset.
 * Renders nothing.
 */
export function ClerkIdentifyBridge(): null {
  const { isLoaded, isSignedIn, userId } = useAuth();

  useEffect(() => {
    if (!isLoaded) return;

    if (isSignedIn && userId) {
      // Only pass the stable Clerk ID. PII like email is intentionally omitted.
      identify(userId);
    } else {
      reset();
    }
  }, [isLoaded, isSignedIn, userId]);

  return null;
}
